import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('dex_token'));
  const [adminToken, setAdminToken] = useState(localStorage.getItem('dex_admin_token'));
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Session timeout (30 minutes of inactivity)
  const SESSION_TIMEOUT = 30 * 60 * 1000;

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/user/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          logout();
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  // Activity tracker for session timeout
  useEffect(() => {
    const handleActivity = () => setLastActivity(Date.now());
    
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);

    const interval = setInterval(() => {
      if (token && Date.now() - lastActivity > SESSION_TIMEOUT) {
        logout();
      }
    }, 60000);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      clearInterval(interval);
    };
  }, [token, lastActivity]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('dex_token', newToken);
    setToken(newToken);
    setUser(userData);
    return response.data;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    return response.data;
  };

  const verifyOTP = async (email, code) => {
    const response = await axios.post(`${API}/auth/verify-otp`, { email, code });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('dex_token', newToken);
    setToken(newToken);
    setUser(userData);
    return response.data;
  };

  const resendOTP = async (email) => {
    const response = await axios.post(`${API}/auth/resend-otp`, { email });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('dex_token');
    setToken(null);
    setUser(null);
  };

  const adminLogin = async (username, password) => {
    const response = await axios.post(`${API}/auth/admin-login`, { username, password });
    const { token: newToken, admin: adminData } = response.data;
    localStorage.setItem('dex_admin_token', newToken);
    setAdminToken(newToken);
    setAdmin(adminData);
    return response.data;
  };

  const adminLogout = () => {
    localStorage.removeItem('dex_admin_token');
    setAdminToken(null);
    setAdmin(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/user/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        console.error('Refresh user failed:', error);
      }
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      admin,
      token,
      adminToken,
      loading,
      login,
      register,
      verifyOTP,
      resendOTP,
      logout,
      adminLogin,
      adminLogout,
      refreshUser,
      isAuthenticated: !!token && !!user,
      isAdminAuthenticated: !!adminToken && !!admin,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
